import React, { useCallback } from 'react';
import { Subscription } from '../../../../types/subscription';
import { colors } from '../../../../colors';
import { useSelector } from 'react-redux';
import { selectActiveCluster } from '../../../../store/slices/subscription';
import {
  formatBalance,
  formatPercentage,
} from '../../../../helpers/NumberFormatter';
import { SummaryInformation as SummaryInformationType } from '../../types';
import { useGetUserAvailableCreditsQuery } from '../../../../store/services/api';
import { selectUserId } from '../../../../store/slices/user';
import LoadingIcons from 'react-loading-icons';
import useWidthBreakpoint from '../../../../hooks/useWidthBreakpoint';

/**
 * @TODO
 * Add total amount invested to overview chart (orange line)
 *
 * Info to add:
 * Summary title + cluster name
 *
 * total change + percentage
 * value + absolute/relative day change
 * current average (daily) profit percentage
 * Nap value + percentage
 *
 * amount of subs,
 * amount of credits
 * total amount of fees paid (to exchange)
 * total amount of stable value + percentage
 */
const SummaryInformation = ({
  subscriptions,
  summaryInformation,
}: {
  subscriptions: Subscription[];
  summaryInformation: SummaryInformationType | undefined;
}) => {
  const userId = useSelector(selectUserId);
  const activeCluster = useSelector(selectActiveCluster);
  const { isMatch } = useWidthBreakpoint();

  const { data: creditsData } = useGetUserAvailableCreditsQuery(userId);

  const clusterName = activeCluster === null ? 'Portfolio' : activeCluster;
  const totalValue = subscriptions.reduce(
    (acc, curr) => acc + curr.totalBalanceInStable,
    0,
  );
  const costBasis = subscriptions.reduce(
    (acc, curr) => acc + (curr.profits?.costBasis.stable || 0),
    0,
  );
  const totalValueChangeAbsolute = totalValue - costBasis;
  const totalValueChangeRelative = (totalValue - costBasis) / costBasis;

  const dayChangeAbsolute = summaryInformation?.dayChange || 0;
  const dayChangeRelative = summaryInformation?.dayChangeRelative || 0;

  const napDifferenceAbsolute = summaryInformation?.napDifference || 0;
  const napDifferenceRelative = summaryInformation?.napDifferenceRelative || 0;

  const averageDailyChangeAbsolute =
    totalValueChangeAbsolute / (summaryInformation?.amountOfDays || 0);
  const averageDailyChangeRelative =
    totalValueChangeRelative / (summaryInformation?.amountOfDays || 0);

  const amountOfSubs = String(subscriptions?.length ?? 0);
  const amountOfCredits = creditsData?.success
    ? `${creditsData.credits} / 50`
    : '';

  const totalFeesPaid = subscriptions.reduce(
    (acc, curr) =>
      acc + (curr.latestTransaction ? curr.latestTransaction.totalFee : 0),
    0,
  );
  const totalStableValueAbsolute = subscriptions.reduce(
    (acc, curr) => acc + curr.stableBalance,
    0,
  );
  const totalStableValueRelative = totalStableValueAbsolute / totalValue;

  const getSign = useCallback((value: number) => {
    if (value > 0) {
      return '+';
    }

    if (value < 0) {
      return '-';
    }

    return '';
  }, []);

  const getColor = useCallback((value: number) => {
    if (value > 0) {
      return colors.green;
    }

    if (value < 0) {
      return colors.red;
    }

    return colors.white;
  }, []);

  console.log({ totalValueChangeRelative });

  return (
    <div
      style={{
        color: colors.white,
        padding: 24,
        paddingTop: 8,
        paddingBottom: isMatch('md') ? 24 : 0,
      }}
    >
      {isMatch('md') && (
        <>
          <p
            style={{ fontSize: 32, fontWeight: 'bold', margin: 0 }}
          >{`${clusterName} Summary`}</p>
          <p style={{ fontSize: 24, margin: 0, marginTop: -12 }}>
            {formatBalance(totalValue, true)}
          </p>
        </>
      )}

      <div
        style={{
          display: 'grid',
          marginTop: 12,
          gridTemplateRows: '1fr 1fr 1fr 1fr',
          gridTemplateColumns: '1fr 1fr',
          columnGap: 16,
        }}
      >
        <Item
          label="Total Value Change"
          value={`${getSign(totalValueChangeAbsolute)}${formatBalance(
            Math.abs(totalValueChangeAbsolute),
            true,
          )}`}
          delta={
            totalValueChangeRelative
              ? formatPercentage(totalValueChangeRelative, 2, true, 'always')
              : undefined
          }
          color={getColor(totalValueChangeAbsolute)}
        />
        <Item
          label="Non-algorithmic Difference"
          value={`${getSign(napDifferenceAbsolute)}${formatBalance(
            Math.abs(napDifferenceAbsolute),
            true,
          )}`}
          delta={formatPercentage(napDifferenceRelative, 2, true, 'always')}
          color={getColor(napDifferenceAbsolute)}
          isLoading={!summaryInformation}
        />
        <Item
          label="Day Change"
          value={`${getSign(dayChangeAbsolute)}${formatBalance(
            Math.abs(dayChangeAbsolute),
            true,
          )}`}
          delta={
            dayChangeRelative
              ? formatPercentage(dayChangeRelative, 2, true, 'always')
              : undefined
          }
          color={getColor(dayChangeAbsolute)}
          isLoading={!summaryInformation}
        />
        <Item
          label="Average Daily Change"
          value={`${getSign(averageDailyChangeAbsolute)}${formatBalance(
            Math.abs(averageDailyChangeAbsolute),
            true,
          )}`}
          delta={
            averageDailyChangeRelative
              ? formatPercentage(averageDailyChangeRelative, 2, true, 'always')
              : undefined
          }
          color={getColor(averageDailyChangeAbsolute)}
          isLoading={!summaryInformation}
        />
        <Item label="Amount of Subscriptions" value={amountOfSubs} />
        <Item
          label="Exchange Fees paid"
          value={formatBalance(totalFeesPaid, true)}
        />
        <Item label="Available Credits" value={amountOfCredits} />
        <Item
          label="Current Stable Value"
          value={`${formatBalance(
            Math.abs(totalStableValueAbsolute),
            true,
          )} | ${formatPercentage(totalStableValueRelative, 2, true, 'never')}`}
        />
      </div>
    </div>
  );
};

export default SummaryInformation;

const Item = ({
  label,
  value,
  delta,
  color,
  isLoading,
}: {
  label: string;
  value: string;
  delta?: string;
  color?: string;
  isLoading?: boolean;
}) => {
  const { isMatch } = useWidthBreakpoint();

  console.log({ delta });

  return (
    <div style={{ color: colors.white, marginBottom: !isMatch('md') ? 4 : 12 }}>
      {/** Label */}
      <p
        style={{
          margin: 0,
          marginTop: 4,
          marginBottom: -4,
          color: colors.gray,
          fontWeight: 600,
          fontSize: 16,
        }}
      >
        {label}
      </p>
      {isLoading ? (
        <div style={{ marginTop: 6 }}>
          <LoadingIcons.Grid width={20} height={20} />
        </div>
      ) : (
        <p
          style={{
            margin: 0,
            fontSize: !isMatch('md') ? 18 : 22,
            color,
            fontWeight: 500,
          }}
        >
          {value}
          {!!delta && (
            <span
              style={{
                backgroundColor: color,
                color: colors.white,
                fontSize: !isMatch('md') ? 14 : 16,
                borderRadius: 6,
                paddingLeft: 8,
                paddingRight: 8,
                marginLeft: 6,
                fontWeight: 700,
                position: 'relative',
                top: -2,
              }}
            >
              {delta}
            </span>
          )}
        </p>
      )}
    </div>
  );
};
